/**
 * Clinician Personalisation Review Queue (Phase 1B-2).
 *
 * Triage of patient personalisation requests that produced an unsupported
 * mismatch against the clinician-approved template. Reuses the clinician
 * dashboard shell + the priority/status card language from the video Review
 * Center, but is a functionally separate surface.
 *
 * A status change here is ONLY a triage decision — it never alters a
 * prescription or approves an unapproved template variation. Backed by
 * GET /api/programme-generator/reviews and PATCH /api/programme-generator/reviews/{id}.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ClinicianLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw, ClipboardCheck, ShieldAlert, TriangleAlert, CheckCircle2,
  XCircle, Clock, Eye, User, ArrowRight,
} from "lucide-react";

const STATUS_TABS = [
  ["all", "All"],
  ["pending", "Pending"],
  ["acknowledged", "Acknowledged"],
  ["resolved", "Resolved"],
  ["declined", "Declined"],
];

const STATUS_META = {
  pending:      { Icon: Clock,        cls: "text-amber-300 border-amber-400/40 bg-amber-400/10",  label: "Pending" },
  acknowledged: { Icon: Eye,          cls: "text-sky-300 border-sky-500/40 bg-sky-500/10",        label: "Acknowledged" },
  resolved:     { Icon: CheckCircle2, cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Resolved" },
  declined:     { Icon: XCircle,      cls: "text-red-300 border-red-500/40 bg-red-500/10",        label: "Declined" },
  superseded:   { Icon: Clock,        cls: "text-zinc-400 border-white/10 bg-white/5",            label: "Superseded" },
};

const PRIORITY_META = {
  red:   { Icon: ShieldAlert,   fg: "text-red-400",   label: "RED" },
  amber: { Icon: TriangleAlert, fg: "text-amber-300", label: "AMBER" },
  green: { Icon: CheckCircle2,  fg: "text-emerald-300", label: "GREEN" },
};

const MISMATCH_LABELS = {
  frequency_not_supported: "Requested training frequency isn't supported by the approved template.",
  duration_exceeded: "Requested session length exceeds the template's approved maximum.",
  equipment_gap: "Required equipment can't be satisfied with the chosen setup (no approved alternative).",
};

const PREF_LABELS = {
  frequency_per_week: "Frequency",
  environment: "Environment",
  equipment: "Equipment",
  max_session_minutes: "Max session length",
  preferred_days: "Preferred days",
  preferred_weekdays: "Preferred days",
  activity_level: "Activity level",
};

const WD = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat", 7: "Sun" };

function fmtPrefValue(key, v) {
  if (v == null || v === "") return "—";
  if (key === "frequency_per_week") return `${v}/week`;
  if (key === "max_session_minutes") return `${v} min`;
  if (Array.isArray(v)) {
    if (key.includes("day") || key.includes("weekday")) return v.map((d) => WD[d] ?? d).join(", ") || "—";
    return v.join(", ") || "—";
  }
  return String(v);
}

export default function ClinicianPersonalisationReviews() {
  const nav = useNavigate();
  const [data, setData] = useState(null); // {reviews, counts}
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/programme-generator/reviews");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load personalisation reviews");
      setData({ reviews: [], counts: {} });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (review, status, note) => {
    setBusy(review.id + status);
    try {
      await api.patch(`/programme-generator/reviews/${review.id}`, { status, note: note || null });
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update review");
    } finally { setBusy(null); }
  };

  const reviews = (data?.reviews || []).filter((r) => r.status !== "superseded");
  const visible = tab === "all" ? reviews : reviews.filter((r) => r.status === tab);
  const counts = data?.counts || {};
  const openCount = (counts.pending || 0) + (counts.acknowledged || 0);

  return (
    <ClinicianLayout>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="fl-label text-[color:var(--fl-orange)] flex items-center gap-2">
            <ClipboardCheck size={13} /> Personalisation Reviews
          </div>
          <h1 className="fl-heading text-3xl md:text-4xl mt-2" data-testid="pr-heading">
            {openCount} request{openCount === 1 ? "" : "s"} awaiting review
          </h1>
          <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>
            Unsupported programme-setup requests · a decision here never changes a prescription or template.
          </div>
        </div>
        <button className="fl-btn-ghost text-sm" onClick={load} data-testid="pr-refresh">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-1 flex-wrap mb-5" role="tablist">
        {STATUS_TABS.map(([id, label]) => {
          const n = id === "all" ? reviews.length : (counts[id] || 0);
          return (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              data-testid={`pr-tab-${id}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition ${
                tab === id ? "bg-[#f47a20] text-black" : "text-zinc-400 hover:text-zinc-100 border border-white/10"
              }`}
            >
              {label} {n > 0 && <span className="opacity-70">· {n}</span>}
            </button>
          );
        })}
      </div>

      {data === null && (
        <div className="fl-card p-6 text-sm" style={{ color: "var(--fl-muted)" }} data-testid="pr-loading">Loading…</div>
      )}
      {data && visible.length === 0 && (
        <div className="fl-card p-10 text-center text-sm" style={{ color: "var(--fl-muted)" }} data-testid="pr-empty">
          Nothing here — no {tab === "all" ? "" : tab + " "}personalisation reviews.
        </div>
      )}

      <div className="grid gap-4">
        {visible.map((r) => (
          <ReviewCard
            key={r.id}
            review={r}
            busy={busy}
            onDecide={decide}
            onOpenPatient={() => nav(`/clinician/patient/${r.patient_id}?tab=cases`)}
          />
        ))}
      </div>
    </ClinicianLayout>
  );
}

function ReviewCard({ review, busy, onDecide, onOpenPatient }) {
  const [noteMode, setNoteMode] = useState(null); // "resolved" | "declined" | null
  const [note, setNote] = useState("");
  const sm = STATUS_META[review.status] || STATUS_META.pending;
  const pm = PRIORITY_META[review.priority] || PRIORITY_META.amber;
  const prefs = review.requested_preferences || {};
  const mismatch = review.mismatch || [];

  const submitNote = () => {
    onDecide(review, noteMode, note.trim());
    setNoteMode(null); setNote("");
  };

  return (
    <div className="fl-card fl-card-elevated p-5" data-testid={`pr-card-${review.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${sm.cls}`} data-testid={`pr-status-${review.id}`}>
              <sm.Icon size={10} /> {sm.label}
            </span>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${pm.fg}`}>
              <pm.Icon size={11} /> {pm.label}
            </span>
          </div>
          <div className="fl-heading text-lg mt-2 flex items-center gap-2">
            <User size={15} className="text-zinc-400" /> {review.patient_name || "Patient"}
          </div>
          <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>{review.patient_email}</div>
          <div className="text-xs text-zinc-400 mt-1">
            Programme: <span className="text-zinc-200">{review.template_title || review.template_slug}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
            {review.created_at ? new Date(review.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
          </div>
        </div>
      </div>

      {/* Requested preferences */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-white/8 p-3">
          <div className="fl-label text-[10px] mb-2">Patient requested</div>
          <ul className="text-xs space-y-1">
            {Object.entries(prefs).filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0)).map(([k, v]) => (
              <li key={k} className="flex gap-2">
                <span className="text-zinc-500 min-w-[120px]">{PREF_LABELS[k] || k}:</span>
                <span className="text-zinc-200">{fmtPrefValue(k, v)}</span>
              </li>
            ))}
            {Object.keys(prefs).length === 0 && <li className="text-zinc-500">Not recorded.</li>}
          </ul>
        </div>
        <div className="rounded-lg border border-amber-400/20 bg-amber-400/5 p-3">
          <div className="fl-label text-[10px] mb-2 text-amber-300">Why it couldn't be generated</div>
          <ul className="text-xs space-y-1.5">
            {mismatch.map((m, i) => (
              <li key={i} className="text-zinc-200 flex gap-2">
                <TriangleAlert size={12} className="text-amber-300 shrink-0 mt-0.5" />
                <span>{m.message || MISMATCH_LABELS[m.type] || m.type || "Unsupported request"}
                  {m.detail && <span className="block text-[11px] text-zinc-500">{m.detail}</span>}
                </span>
              </li>
            ))}
            {mismatch.length === 0 && <li className="text-zinc-500">No mismatch detail recorded.</li>}
          </ul>
        </div>
      </div>

      {/* Decision record */}
      {review.decision_note && (
        <div className="mt-3 text-xs rounded-lg border border-white/8 p-3">
          <span className="text-zinc-500">Clinician note: </span>
          <span className="text-zinc-200">{review.decision_note}</span>
          {review.decided_by_name && <span className="text-zinc-500"> — {review.decided_by_name}</span>}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button className="fl-btn-ghost text-xs" onClick={onOpenPatient} data-testid={`pr-open-patient-${review.id}`}>
          <ArrowRight size={12} /> Open patient & programme
        </button>
        {review.status === "pending" && (
          <button className="fl-btn-ghost text-xs" disabled={busy === review.id + "acknowledged"}
            onClick={() => onDecide(review, "acknowledged")} data-testid={`pr-ack-${review.id}`}>
            <Eye size={12} /> Acknowledge
          </button>
        )}
        {review.status !== "resolved" && (
          <button className="fl-btn text-xs" onClick={() => { setNoteMode("resolved"); setNote(""); }} data-testid={`pr-resolve-${review.id}`}>
            <CheckCircle2 size={12} /> Resolve
          </button>
        )}
        {review.status !== "declined" && (
          <button className="fl-btn-ghost text-xs text-red-300 border-red-500/30" onClick={() => { setNoteMode("declined"); setNote(""); }} data-testid={`pr-decline-${review.id}`}>
            <XCircle size={12} /> Decline
          </button>
        )}
      </div>

      {noteMode && (
        <div className="mt-3 rounded-lg border border-white/10 p-3" data-testid={`pr-note-box-${review.id}`}>
          <div className="text-xs text-zinc-300 mb-2">
            {noteMode === "resolved"
              ? "How was this resolved? (e.g. offered an approved alternative, adjusted via the programme editor, asked for revised preferences)"
              : "Explain why this configuration can't be provided as requested."}
          </div>
          <textarea
            className="fl-input text-xs w-full" rows={2} value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a short note for the record (optional)…"
            data-testid={`pr-note-${review.id}`}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button className="fl-btn-ghost text-xs" onClick={() => setNoteMode(null)}>Cancel</button>
            <button className="fl-btn text-xs" disabled={busy === review.id + noteMode}
              onClick={submitNote} data-testid={`pr-note-confirm-${review.id}`}>
              Confirm {STATUS_META[noteMode]?.label}
            </button>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">
            This records a triage decision only — it does not change any prescription or approve a template variation.
          </div>
        </div>
      )}
    </div>
  );
}
